import { createSelector } from 'reselect';
import { sortBy } from 'lodash';

import { IProfile, IProfileKYC, TMultipleLoading } from '@/store/models/profile.model';
import { IState } from '@/store/types/search.types';
import { ITaxResidence } from '@/store/models/taxResidence.model';
import { TRestrictionsGroupType } from '@/store/models/restriction.model';
import { BeneficiaryTools } from '@/utils/tools/BeneficiaryTools';
import { getSupportId } from '@/features/dashboard-users/helpers/getSupportId';
import { TAppState } from '@/store/reducers/reducers';

export const selectProfileByUserId = (state: TAppState, userId: number) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId);

export const selectBeneficiariesByUserId = (userId: number) => (state: TAppState) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId)?.state?.beneficiaries;

export const selectProfileCardsByUserId = (state: TAppState, userId: number) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId)?.state?.profileCards;

export const selectWalletOperationReportByUserIdAndWalletId = (state: TAppState, userId: number) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId)?.state?.operations;

export const selectProfileWalletsByUserId = (state: TAppState, userId: number) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId)?.state?.profileWallets;

export const selectWalletsByUserId = (state: TAppState, userId: number) =>
  state.profile.profiles.state
    ?.find((profile) => profile.state?.user.state?.userId === userId)
    ?.state?.profileWallets?.state?.map?.((profileWalletState) => profileWalletState.state?.wallet);

export const selectProfileKYCByUserId = (state: TAppState, userId: number) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId)?.state?.profileKYCs;

export const selectProfileCardByUserIdAndCardId = (state: TAppState, userId: number, cardId: number) =>
  state.profile.profiles.state
    ?.find((profile) => profile.state?.user.state?.userId === userId)
    ?.state?.profileCards?.state?.find((profileCard) => profileCard.state?.card.cardId === cardId);

export const selectProfileWalletByUserIdAndWalletId = (state: TAppState, userId: number, walletId: number) =>
  state.profile.profiles.state
    ?.find((profile) => profile.state?.user.state?.userId === userId)
    ?.state?.profileWallets?.state?.find((profileWallet) => profileWallet.state?.wallet.walletId === walletId);

export const selectProfileKYCDocumentsByUserId = (state: TAppState, userId: number) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId)?.state?.profileKYCs
    ?.state?.documents;

export const selectCurrentProfileWalletByUserId = (state: TAppState, userId: number) => {
  const userProfile = state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId);
  return userProfile?.state?.profileWallets?.state?.find(
    (profileWallet) => profileWallet.state?.wallet.walletId === userProfile.state?.profileWallets?.currentWalletId,
  );
};

export const selectCurrentProfileWalletByUserIdAndWalletId = (state: TAppState, userId: number, walletId: number) => {
  const userProfile = state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId);
  return userProfile?.state?.profileWallets?.state?.find(
    (profileWallet) => profileWallet.state?.wallet.walletId === walletId,
  );
};

export const selectCurrentProfileCardByUserId = (state: TAppState, userId: number) => {
  const userProfile = state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId);
  return userProfile?.state?.profileCards?.state?.find(
    (profileCard) => profileCard.state?.card.cardId === userProfile.state?.profileCards?.currentCardId,
  );
};

export const getCurrentCardId = (state: TAppState, userId: number) =>
  state.profile.profiles.state?.find((profile) => profile.state?.user.state?.userId === userId)?.state?.profileCards
    ?.currentCardId;

export const selectProfileBeneficiaryByUserIdAndBeneficiaryId = (
  state: TAppState,
  userId: number,
  beneficiaryId: number,
) => {
  return state.profile.profiles.state
    ?.find((profile) => profile.state?.user.state?.userId === userId)
    ?.state?.beneficiaries?.state?.find((beneficiary) => beneficiary.state?.beneficiary.id === beneficiaryId);
};

export const selectProfileBeneficiaryByType = createSelector(
  selectProfileBeneficiaryByUserIdAndBeneficiaryId,
  (beneficiaryState) => BeneficiaryTools.excludeBlacklistUmr(beneficiaryState?.state?.beneficiary),
);

export const selectWalletsbyUserId = createSelector(selectProfileWalletsByUserId, (walletState) =>
  walletState?.state?.map((wallet) => wallet.state?.wallet),
);

export const selectMetadataByRestrictionsGroupId = (
  state: TAppState,
  restrictionGroupType: TRestrictionsGroupType,
  restrictionGroupId: number,
) => {
  return state.predefRestrictionsGroup.restrictionsGroups[restrictionGroupType]?.elements?.find(
    (restrictionGroup) => restrictionGroup.group.id === restrictionGroupId,
  );
};

export const selectProfileKycDocumentByUserIdAndDocumentId = (state: TAppState, userId: number, documentId: string) =>
  selectProfileKYCDocumentsByUserId(state, userId)?.state.find(
    (kycDocument) => kycDocument.document.documentId === documentId,
  );

export const selectKycNeedReviewDocumentDocumentId = (state: TAppState, documentId: string) =>
  state.kycNeedReview.documents?.elements?.find(
    (needReviewDocument) => needReviewDocument.state?.document.documentId === documentId,
  );

export const selectTemplateGroupByGroupAndName = (state: TAppState, group: string, name: string) =>
  state.template.templateGroups?.state?.[group]?.find((templateGroup) => templateGroup.state?.template.name === name);

export const selectAndSortDashoardUsers = createSelector(
  (state: TAppState) => state.supportUser.dashboardUsers?.elements,
  (dashboardUsers) => {
    const sortedDashboardUsers = sortBy(
      dashboardUsers?.map((dashboardUser) => dashboardUser.state),
      'username',
    );
    if (!sortedDashboardUsers?.length) return [];
    const currentDashboardUserId = getSupportId();
    if (currentDashboardUserId) {
      const currentDashboardUser = sortedDashboardUsers.find(
        (dashboardUser) => dashboardUser?.id === currentDashboardUserId,
      );
      if (currentDashboardUser)
        return [
          currentDashboardUser,
          ...sortedDashboardUsers.filter((dashboardUser) => dashboardUser?.id !== currentDashboardUserId),
        ];
    }
    return sortedDashboardUsers;
  },
);

export const selectAndSortSuspendedDashoardUsers = createSelector(
  (state: TAppState) => state.supportUser.suspendedDashboardUsers?.elements,
  (dashboardUsers) => {
    const sortedDashboardUsers = sortBy(
      dashboardUsers?.map((dashboardUser) => dashboardUser.state),
      'username',
    );
    if (!sortedDashboardUsers?.length) return [];
    return sortedDashboardUsers;
  },
);

export const memoizedSelectAndSortDashoardUsers = createSelector(
  selectAndSortDashoardUsers,
  (stateElement) => stateElement,
);

export const selectDashboardUser = (dashboardUserId: string) => (state: TAppState) =>
  state.supportUser.dashboardUsers.elements.find((dUser) => dUser.state?.id === dashboardUserId);

export const selectSuspendedDashboardUser = (dashboardUserId: string) => (state: TAppState) =>
  state.supportUser.suspendedDashboardUsers.elements.find((dUser) => dUser.state?.id === dashboardUserId);

export const selectSCAState = (state: TAppState, userId: number) =>
  selectProfileByUserId(state, userId)?.state?.scaWallets;

export const selectSCADeletedState = (state: TAppState, userId: number) =>
  selectProfileByUserId(state, userId)?.state?.deletedScaWallets;

export const selectSCAWallets = createSelector(selectSCAState, (scaWallets) => {
  return scaWallets?.state?.map((scaState) => scaState.state);
});

export const selectSCAByscaWalletId = (state: TAppState, userId: number, scaWalletId: string) =>
  selectProfileByUserId(state, userId)?.state?.scaWallets?.state?.find(
    (scaState) => scaState.state?.scaWalletId === scaWalletId,
  );

export const selectTaxResidencesState = (state: TAppState, userId: number) =>
  selectProfileByUserId(state, userId)?.state?.taxResidences;

export const selectTaxResidencesByUserId = createSelector(selectTaxResidencesState, (taxeResidences) => {
  return taxeResidences?.state
    ?.map((taxeResidence) => taxeResidence.state)
    .filter((taxeResidence) => !taxeResidence.isDeleted);
});

export const isUserTaxResidencesLoading = createSelector(
  selectTaxResidencesState,
  (taxResidences) =>
    taxResidences?.state?.some((taxResidence) => !!taxResidence.isLoading) || taxResidences?.create?.isLoading,
);

export const selectPredefRestrictionGroup = createSelector(
  [(state: TAppState) => state.predefRestrictionsGroup.restrictionsGroups],
  (restrictionsGroups) => restrictionsGroups,
);

export const selectPredefRestrictionGroupsByType = createSelector(
  [selectPredefRestrictionGroup, (_state: TAppState, type: TRestrictionsGroupType) => type],
  (restrictionsGroups, type) => restrictionsGroups[type],
);

export const selectMembersTaxResidences = createSelector(
  (state: TAppState, parentId: number) => {
    const parentProfile = selectProfileByUserId(state, parentId);
    return parentProfile?.state?.children?.state || [];
  },
  (state: TAppState) => state,
  (children: IState<Omit<IProfile, 'children'>>[], _state: TAppState) => {
    return children.reduce(
      (acc: IState<IState<ITaxResidence>[]> & { create?: IState<TMultipleLoading> }, currentChild) => {
        const currentTaxResidence = currentChild.state.taxResidences;
        return {
          state: [...acc.state, ...(currentTaxResidence?.state || [])],
          create: { ...acc.create, state: { ...acc?.create?.state, ...currentTaxResidence?.create?.state } },
        };
      },
      { state: [], create: {} },
    );
  },
);

export const selectMembersKycDocuments = createSelector(
  (state: TAppState, parentId: number) => {
    const parentProfile = selectProfileByUserId(state, parentId);
    return parentProfile?.state?.children?.state || [];
  },
  (children: IState<Omit<IProfile, 'children'>>[]) => {
    return children.reduce(
      (
        acc: IState<IProfileKYC[]> & {
          create?: IState<TMultipleLoading>;
        },
        currentChild,
      ) => {
        const currentKycDoc = currentChild.state.profileKYCs?.state?.documents;
        return {
          state: [...acc.state, ...(currentKycDoc?.state || [])],
          create: { ...acc.create, state: { ...acc?.create?.state, ...currentKycDoc?.create?.state } },
        };
      },
      { state: [], create: {} },
    );
  },
);

export const selectProofOfPayout = createSelector(
  [
    selectProfileWalletsByUserId,
    (_, __, walletId: number) => walletId,
    (_, __, ___, operationId: string) => operationId,
  ],
  (walletsState, walletId, operationId) => {
    return walletsState.state
      .find((walletSate) => walletSate.state.wallet.walletId === walletId)
      ?.state?.paginateOperations?.state?.elements?.find((operation) => operation.objectId === operationId);
  },
);
